import { useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Loader2, Trash2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RoleGate } from '@/components/auth/RoleGate';
import { Role } from '@jeket-cloud/types/auth';
import { MachineStatusBadge } from '@/components/deployments/MachineStatusBadge';
import { RemoveMachineDialog } from '@/components/deployments/RemoveMachineDialog';
import { IndicatorLine } from '../cluster/BoxRow';
import { siderolinkIndicator, talosApiIndicator, kubeletHeartbeatIndicator } from '@/lib/boxes';
import type { Machine, MachineRole, MachineStatus, SitePod } from '@/types';
import { PodTable } from './PodTable';
import { timeAgo } from './shared';

/**
 * Granular provisioning steps (issue #26).
 *
 * The 6-step flow surfaces every distinct phase a machine goes through:
 *   0. Enrolled              — hardware discovered and matched to a template
 *   1. Config pending        — staged provisioning is enabled and a human
 *                              still has to click "Deploy" (only shown for
 *                              `config_pending`; #24 will toggle this state)
 *   2. Config pushed         — Talos config delivered, node is rebooting
 *   3. Bootstrapping         — etcd bootstrap is running on the lead CP
 *   4. Installing prereqs    — platform components (CNI, Longhorn, etc.) are
 *                              being applied
 *   5. Active                — node is Ready in K8s and the cluster is live
 *
 * Legacy `configuring` is treated as a synonym for "config_pushed" and maps
 * to step 2 for backward compatibility with rows already in flight.
 */
const PROVISION_STEPS = [
  'Enrolled',
  'Config pending',
  'Config pushed',
  'Bootstrapping',
  'Installing prereqs',
  'Active',
] as const;

function machineStatusToStep(status: MachineStatus): number {
  switch (status) {
    case 'discovering':
    case 'pending_enrollment':
    case 'registered':
    case 'assigned':
      return 0;
    case 'config_pending':
      return 1;
    case 'configuring':
    case 'config_pushed':
    case 'config_apply_failed':
      return 2;
    case 'bootstrapping':
    case 'joining':
      return 3;
    case 'installing_prereqs':
      return 4;
    case 'ready':
      return 5;
    default:
      return -1; // not in provisioning flow
  }
}

/**
 * Whether the "Config pending" step is meaningful for a given machine.
 * Currently only true when the machine is itself in `config_pending`.
 * Once #24 (staged provisioning toggle) lands, this will also be true when
 * the parent site has staged provisioning enabled.
 */
function showsConfigPendingStep(status: MachineStatus): boolean {
  return status === 'config_pending';
}

function ProvisioningStepper({
  status,
  errorMessage,
  onTalosReset,
}: {
  status: MachineStatus;
  errorMessage: string | null;
  /** When provided and the stepper is in an error state, renders a
   *  "Reset to maintenance" link as the escape hatch. */
  onTalosReset?: () => void;
}) {
  const currentStep = machineStatusToStep(status);
  if (currentStep < 0) return null;

  const isError = status === 'error' || status === 'config_apply_failed';
  const includeConfigPending = showsConfigPendingStep(status);

  // Hide the staged-only "Config pending" step (index 1) for the direct
  // path so the stepper reads as a clean 5-step flow until #24 enables
  // staged provisioning per-site.
  const visibleSteps = PROVISION_STEPS.map((label, i) => ({ label, originalIndex: i }))
    .filter(({ originalIndex }) => originalIndex !== 1 || includeConfigPending);

  return (
    <div className="flex items-center gap-1 mb-3">
      {visibleSteps.map(({ label, originalIndex }, displayIndex) => {
        const completed = originalIndex < currentStep;
        const active = originalIndex === currentStep && !isError;
        const errored = isError && originalIndex === currentStep;

        return (
          <div key={label} className="flex items-center gap-1">
            {displayIndex > 0 && <div className={cn('w-6 h-px', completed ? 'bg-status-healthy' : 'bg-surface-elevated')} />}
            <div className="flex items-center gap-1.5">
              {completed ? (
                <Check className="w-3.5 h-3.5 text-status-healthy" />
              ) : active ? (
                <Loader2 className="w-3.5 h-3.5 text-gold animate-spin" />
              ) : errored ? (
                <XCircle className="w-3.5 h-3.5 text-status-error" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border border-silver-muted/40" />
              )}
              <span className={cn(
                'text-[10px]',
                completed ? 'text-status-healthy' : active ? 'text-gold' : errored ? 'text-status-error' : 'text-silver-muted'
              )}>
                {label}
              </span>
            </div>
          </div>
        );
      })}
      {isError && errorMessage && (
        <span className="ml-2 text-[10px] text-status-error">{errorMessage}</span>
      )}
      {isError && onTalosReset && (
        <button
          onClick={onTalosReset}
          className="ml-2 text-[10px] text-status-error underline hover:text-status-error/80 transition-colors"
        >
          Reset to maintenance
        </button>
      )}
    </div>
  );
}

/**
 * A role change Jeket made that the box has not taken yet (JSS#977): the role
 * is rendered into the box's config, so it runs its old one until it is
 * reinstalled. Without this the row showed the new role on a box still
 * running the old one.
 */
function RolePendingNote({ machine: m }: { machine: Machine }) {
  if (!m.rolePendingReinstall) return null;
  const runs = m.configRole === 'controlplane' ? 'a control plane' : 'a worker';
  return (
    <div
      className="mt-1 text-[11px] text-status-warning"
      title={`Jeket changed this box's role. It keeps running as ${runs} until it is reinstalled: reset it to ` +
        'maintenance and it reinstalls itself with the new role.'}
    >
      role change pending reinstall
    </div>
  );
}

function RoleBadge({ role }: { role: MachineRole | null }) {
  if (!role) return <span className="text-xs text-silver-muted">unassigned</span>;

  const isCP = role === 'controlplane';
  const label = isCP ? 'CP' : 'Worker';

  return (
    <span
      className={cn(
        'px-2 py-0.5 rounded text-xs font-medium',
        isCP
          ? 'bg-gold/10 text-gold border border-gold/20'
          : 'bg-surface-elevated text-silver-bright'
      )}
    >
      {label}
    </span>
  );
}

// ---- Node Table (expandable rows with details) ----

/**
 * The node inventory's table. Read-only about roles (JSS#874): every box is
 * a JBS box, and JSS makes the first boxes to join a site its control planes
 * (up to the count set at site creation) and the rest workers. The per-box
 * role selector and Assign / Unassign are gone; deployment-service refuses
 * them, and Jeket staff override a role from the staff console.
 */
export function NodeTable({
  machines,
  pods,
  onTalosReset,
}: {
  machines: Machine[];
  pods?: SitePod[];
  onTalosReset?: (machine: Machine) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Machine | null>(null);

  if (machines.length === 0) {
    return (
      <div className="p-6 text-center text-silver-muted text-sm">No nodes found</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-elevated text-left">
            <th className="w-8 px-2 py-2.5" />
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Hostname</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Role</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Status</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted">Health</th>
            <th className="px-4 py-2.5 text-xs font-medium text-silver-muted text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {machines.map((m) => {
            const isExpanded = expandedId === m.id;
            return (
              <NodeRow
                key={m.id}
                machine={m}
                isExpanded={isExpanded}
                onToggle={() => setExpandedId(isExpanded ? null : m.id)}
                pods={pods}
                onDelete={(machine) => setConfirmDelete(machine)}
                onTalosReset={onTalosReset}
              />
            );
          })}
        </tbody>
      </table>

      {confirmDelete && (
        <RemoveMachineDialog machine={confirmDelete} onClose={() => setConfirmDelete(null)} />
      )}
    </div>
  );
}

function NodeRow({
  machine: m,
  isExpanded,
  onToggle,
  pods,
  onDelete,
  onTalosReset,
}: {
  machine: Machine;
  isExpanded: boolean;
  onToggle: () => void;
  pods?: SitePod[];
  onDelete: (machine: Machine) => void;
  onTalosReset?: (machine: Machine) => void;
}) {
  return (
    <>
      <tr
        className={cn(
          'border-b border-surface-elevated hover:bg-surface-elevated/30 transition-colors cursor-pointer',
          isExpanded && 'bg-surface-elevated/20',
        )}
        onClick={onToggle}
      >
        <td className="px-2 py-3 text-center">
          {isExpanded ? (
            <ChevronDown size={14} className="text-gold mx-auto" />
          ) : (
            <ChevronRight size={14} className="text-silver-muted mx-auto" />
          )}
        </td>
        <td className="px-4 py-3">
          <span className="text-silver-bright font-mono text-xs">{m.hostname}</span>
        </td>
        <td className="px-4 py-3">
          <RoleBadge role={m.role} />
          <RolePendingNote machine={m} />
        </td>
        <td className="px-4 py-3">
          <MachineStatusBadge status={m.status} />
        </td>
        {/* Three layers, not one "Last seen" (#789/#804): the SideroLink
            handshake alone made a box with a dead Talos API look alive. */}
        <td className="px-4 py-3 min-w-[13rem]">
          <IndicatorLine indicator={siderolinkIndicator(m, Date.now())} />
          <IndicatorLine indicator={talosApiIndicator(m, Date.now())} />
          <IndicatorLine indicator={kubeletHeartbeatIndicator(m, Date.now())} />
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
            {(m.status === 'registered' || m.status === 'decommissioned') && (
              <RoleGate role={Role.Admin}>
                <button
                  onClick={() => onDelete(m)}
                  className="text-silver-muted hover:text-status-error transition-colors"
                  aria-label={`Remove ${m.hostname}`}
                  title="Remove and block from rejoining"
                >
                  <Trash2 size={14} />
                </button>
              </RoleGate>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-surface-elevated">
          <td colSpan={6} className="px-0 py-0">
            <div className="bg-surface-deep/50 px-6 py-4 space-y-4">
              {/* Provisioning stepper — shown during active provisioning */}
              <ProvisioningStepper
                status={m.status}
                errorMessage={m.errorMessage}
                onTalosReset={onTalosReset ? () => onTalosReset(m) : undefined}
              />

              {/* Provisioning timestamps */}
              {(m.configPushedAt || m.siderolinkLastSeen) && (
                <div className="flex gap-4 text-[10px] text-silver-muted">
                  {m.configPushedAt && (
                    <span>Config pushed: <span className="text-silver-bright">{timeAgo(m.configPushedAt)}</span></span>
                  )}
                  {m.siderolinkLastSeen && (
                    <span>SideroLink last seen: <span className="text-silver-bright">{timeAgo(m.siderolinkLastSeen)}</span></span>
                  )}
                </div>
              )}

              {/* Warning banner for pre-flight issues */}
              {m.errorMessage && m.status !== 'error' && (
                <div className="flex items-center gap-2 bg-status-warning/10 border border-status-warning/20 rounded px-3 py-2 text-xs text-status-warning">
                  <AlertTriangle size={14} />
                  {m.errorMessage}
                </div>
              )}

              {/* Pre-flight network warnings (issue #23). Each warning gets
                  its own line; 'block' severity is rendered in error red so
                  operators can see at a glance which checks gated the push. */}
              {(m.prereqWarnings?.length ?? 0) > 0 && (
                <div className="space-y-1">
                  {m.prereqWarnings!.map((w, idx) => (
                    <div
                      key={idx}
                      className={
                        w.severity === 'block'
                          ? 'flex items-center gap-2 bg-status-error/10 border border-status-error/30 rounded px-3 py-2 text-xs text-status-error'
                          : 'flex items-center gap-2 bg-status-warning/10 border border-status-warning/20 rounded px-3 py-2 text-xs text-status-warning'
                      }
                    >
                      <AlertTriangle size={14} />
                      <span>
                        <span className="font-mono uppercase mr-2">[{w.type} {w.severity}]</span>
                        {w.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Node Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Hardware */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-silver-bright uppercase tracking-wider">Hardware</h4>
                  <div className="space-y-1.5 text-xs">
                    {m.hardware.cpu && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">CPU</span>
                        <span className="text-silver-bright font-mono">{m.hardware.cpu}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-silver-muted">Cores</span>
                      <span className="text-silver-bright font-mono">{m.hardware.cpuCores || '--'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-silver-muted">Memory</span>
                      <span className="text-silver-bright font-mono">
                        {m.hardware.memoryBytes > 0
                          ? `${Math.round(m.hardware.memoryBytes / (1024 * 1024 * 1024))} GB`
                          : '--'}
                      </span>
                    </div>
                    {m.hardware.gpus.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">GPUs</span>
                        <span className="text-silver-bright font-mono">
                          {m.hardware.gpus.map((g) => `${g.name} (${g.vram})`).join(', ')}
                        </span>
                      </div>
                    )}
                    {m.hardware.disks.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">Disks</span>
                        <span className="text-silver-bright font-mono text-right">
                          {m.hardware.disks.map((d) => (
                            <span key={d.name}>
                              {d.name} {Math.round(d.sizeBytes / (1024 * 1024 * 1024))}GB {d.type}
                              {(d as any).systemDisk && (
                                <span className="text-status-warning ml-1" title="Disk has existing Talos system">
                                  <AlertTriangle size={10} className="inline" />
                                </span>
                              )}
                              {', '}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Node Info */}
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-silver-bright uppercase tracking-wider">Node Info</h4>
                  <div className="space-y-1.5 text-xs">
                    {m.enrollmentStatus && m.enrollmentStatus !== 'none' && (
                      <div className="flex justify-between items-center">
                        <span className="text-silver-muted">Enrollment</span>
                        <EnrollmentStatusBadge status={m.enrollmentStatus} />
                      </div>
                    )}
                    {m.talosVersion && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">Talos</span>
                        <span className="text-silver-bright font-mono">{m.talosVersion}</span>
                      </div>
                    )}
                    {m.kubernetesVersion && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">Kubernetes</span>
                        <span className="text-silver-bright font-mono">{m.kubernetesVersion}</span>
                      </div>
                    )}
                    {m.platform && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">Platform</span>
                        <span className="text-silver-bright font-mono">{m.platform}</span>
                      </div>
                    )}
                    {m.errorMessage && (
                      <div className="flex justify-between">
                        <span className="text-silver-muted">Error</span>
                        <span className="text-status-error font-mono">{m.errorMessage}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Discovered Hardware (for machines with discovered hardware) */}
              {(m.enrollmentStatus === 'discovered' || m.enrollmentStatus === 'pending_template') &&
                (m.hardware.disks.length > 0 || m.hardware.nics.length > 0 || m.hardware.gpus.length > 0) && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-silver-bright uppercase tracking-wider">
                    Discovered Hardware
                  </h4>
                  <div className="bg-surface border border-surface-elevated rounded-lg p-3 space-y-2 text-xs">
                    {m.hardware.disks.length > 0 && (
                      <div>
                        <span className="text-silver-muted">Disks:</span>
                        <div className="mt-1 space-y-0.5">
                          {m.hardware.disks.map((d, i) => (
                            <div key={i} className="flex justify-between font-mono text-silver-bright">
                              <span>{d.name}</span>
                              <span>{Math.round(d.sizeBytes / (1024 * 1024 * 1024))}GB {d.type} {d.model && `(${d.model})`}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {m.hardware.nics.length > 0 && (
                      <div>
                        <span className="text-silver-muted">NICs:</span>
                        <div className="mt-1 space-y-0.5">
                          {m.hardware.nics.map((n, i) => (
                            <div key={i} className="flex justify-between font-mono text-silver-bright">
                              <span>{n.name} ({n.mac})</span>
                              <span>{n.speed} {n.driver}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {m.hardware.gpus.length > 0 && (
                      <div>
                        <span className="text-silver-muted">GPUs:</span>
                        <div className="mt-1 space-y-0.5">
                          {m.hardware.gpus.map((g, i) => (
                            <div key={i} className="flex justify-between font-mono text-silver-bright">
                              <span>{g.name}</span>
                              <span>{g.vendor} {g.vram}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pods for this node (JBS namespace) */}
              {pods && pods.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-silver-bright uppercase tracking-wider">
                    Pods (jbs namespace)
                  </h4>
                  <div className="bg-surface border border-surface-elevated rounded-lg">
                    <PodTable pods={pods} namespace="jbs" nodeName={m.hostname} />
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---- Enrollment Status Badge ----

function EnrollmentStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    none: 'bg-silver-muted/20 text-silver-muted',
    discovered: 'bg-status-warning/20 text-status-warning',
    pending_template: 'bg-gold/20 text-gold',
    enrolled: 'bg-status-healthy/20 text-status-healthy',
  };

  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', colors[status] ?? colors.none)}>
      {status.replace('_', ' ')}
    </span>
  );
}
